import { useContext } from "react";
import { AppStateContext } from "../state/AppProvider";
import { initializeTeams, authenticateWithTeams } from "../utils/teamsAuth";
import { buildB2CScope, clearTeamsAccessToken, getB2CClaims, setTeamsAccessToken } from "../utils/token";
import { assignUserToProgram, assignExternalProgram, removeExternalProgram, removeCase } from "../api";
import { getEnvContext } from "../utils/envContext";

interface UseB2CAuthOptions {
    setPopUpBlocker: (val: boolean) => void;
}

export const useB2CAuth = ({ setPopUpBlocker }: UseB2CAuthOptions) => {
    const appStateContext = useContext(AppStateContext);
    const { msalInstance, b2cAuthResponse } = appStateContext?.state || {};
    const branding = appStateContext?.state.frontendSettings?.branding;
    const settings = appStateContext?.state.frontendSettings;

    const handleB2CSignIn = async () => {
        if (!msalInstance) {
            console.error("MSAL instance is not initialized.");
            return;
        }

        try {
            const inTeams = await initializeTeams();

            if (inTeams) {
                // In Teams, loginPopup doesn't work — use the Teams-managed popup flow instead
                const azure_b2c_client_id = branding?.azure_b2c_client_id;
                const azure_b2c_userflow_endpoint = branding?.azure_b2c_userflow_endpoint;
                const b2cTenantName = branding?.b2c_tenant_subdomain || "";
                const apiScope = buildB2CScope(azure_b2c_client_id!, b2cTenantName);

                if (!azure_b2c_client_id || !azure_b2c_userflow_endpoint) {
                    console.error("Missing B2C config for Teams sign-in.");
                    return;
                }

                const teamsResult = await authenticateWithTeams(azure_b2c_client_id, azure_b2c_userflow_endpoint, apiScope);
                if (!teamsResult.encryptedToken) {
                    throw new Error("No encrypted token returned from Teams auth");
                }
                setTeamsAccessToken(teamsResult.encryptedToken, teamsResult.accessTokenExpiry ?? undefined);
                appStateContext?.dispatch({
                    type: 'SET_B2C_AUTH_RESPONSE',
                    payload: { instance: msalInstance, response: { ...teamsResult, idTokenClaims: teamsResult.account?.idTokenClaims } }
                });
            } else {
                const loginRequest = {
                    scopes: ["openid", "profile", "email"],
                    prompt: "select_account",
                    codeChallengeMethod: "S256",
                };

                let isInIframe = false;
                try {
                    isInIframe = window.self !== window.top;
                } catch (e) {
                    isInIframe = true;
                }

                if (isInIframe) {
                    await handleIframeSignIn();
                } else {
                    await msalInstance.loginRedirect(loginRequest);
                }
            }

            setPopUpBlocker(false);
        } catch (error) {
            console.error("Sign-in error:", error);
        }
    };

    // Sign-in handler for the cross-origin iframe gate. Mirrors the iframe branch of
    // handleB2CSignIn but uses the API scope (so we get an access token) and runs the
    // shared post-auth flow to land in the same state as a top-level redirect login.
    const handleIframeSignIn = async () => {
        if (!msalInstance) {
            console.error("MSAL instance is not initialized.");
            return;
        }

        try {
            const azure_b2c_client_id = branding?.azure_b2c_client_id;
            if (!azure_b2c_client_id) {
                console.error("Missing B2C client id for iframe sign-in.");
                return;
            }
            const apiScope = buildB2CScope(azure_b2c_client_id, branding?.b2c_tenant_subdomain || "");

            // Clear any stale interaction-in-progress flag in localStorage. We skip
            // handleRedirectPromise during iframe init, so a prior incomplete interaction
            // can leave MSAL refusing new ones with "interaction_in_progress".
            await msalInstance.handleRedirectPromise().catch(() => {});

            const response = await msalInstance.loginPopup({
                scopes: [apiScope],
                prompt: "select_account",
            });

            if (response) {
                await appStateContext?.completeB2CAuthFlow(msalInstance, response);
            }
        } catch (error) {
            console.error("Iframe sign-in error:", error);
        }
    };

    const handleB2CSignOut = async () => {
        if (!msalInstance) {
            console.error("MSAL instance is not initialized.");
            return;
        }

        const inTeams = await initializeTeams();

        if (inTeams) {
            clearTeamsAccessToken();
            msalInstance.clearCache();
            window.location.reload();
            return;
        }

        const logoutRequest = {
            postLogoutRedirectUri: branding?.azure_b2c_redirect_uri,
            //postLogoutRedirectUri: 'http://localhost:50505/', // for testing locally
            authority: msalInstance.getConfiguration().auth.authority,
        };

        try {
            let isInIframe = false;
            try {
                isInIframe = window.self !== window.top;
            } catch (e) {
                isInIframe = true;
            }

            // Use popup for iframe scenarios, redirect otherwise
            if (isInIframe) {
                await msalInstance.logoutPopup(logoutRequest);
                window.location.reload();
            } else {
                await msalInstance.logoutRedirect(logoutRequest);
            }
        } catch (error) {
            console.error("Sign-out error:", error);
        }
    };

    // Handle internal program assignment for B2C authenticated users
    const handleInternalProgramAssignment = async (programConfigCode: string, programId?: string) => {
        if (!programConfigCode && !programId) {
            console.error("No program config code or id provided for internal assignment");
            return false;
        }

        if (!b2cAuthResponse?.account?.localAccountId) {
            console.error("User not authenticated for internal program assignment");
            return false;
        }

        try {
            const response = await assignUserToProgram(
                programConfigCode,
                msalInstance,
                settings,
                programId
            );

            if (response && response.success) {
                // Update state to show pending access screen
                appStateContext?.dispatch({
                    type: 'SET_B2C_INTERNAL_AUTH_STATE',
                    payload: 'pending'
                });
                // Update frontend settings to reflect pending state
                if (appStateContext?.state.frontendSettings) {
                    appStateContext?.dispatch({
                        type: 'FETCH_FRONTEND_SETTINGS',
                        payload: {
                            ...appStateContext.state.frontendSettings,
                            is_pending_access: true,
                            matched_program_name: response.program_name || null,
                            filtered_internal_programs: []
                        }
                    });
                }
                return true;
            } else {
                console.error("Program assignment failed:", response?.message);
                return false;
            }
        } catch (error) {
            console.error("Program assignment error:", error);
            return false;
        }
    };

    const hasInternalPrograms = settings?.has_internal_programs === true;

    interface ProgramMutationResult {
        success: boolean;
        external_programs?: string[];
        profile?: any;
        template?: any;
    }

    const handleExternalProgramAssignment = async (
        programCode: string,
        authFieldsVal: { [key: string]: string }
    ): Promise<ProgramMutationResult> => {
        // Prefer the B2C claim oid when authFieldsVal.oid hasn't been populated yet
        // (happens when branding.show_signin !== "Y" and the sync effect never ran).
        const claimOid = getB2CClaims(b2cAuthResponse)?.oid;
        const oid = authFieldsVal?.oid || claimOid;
        if (!programCode || !oid) {
            console.error("Missing programCode or oid for external program assignment");
            return { success: false };
        }

        const payload = { ...(authFieldsVal || {}), oid };
        try {
            const { env, isInIframe } = getEnvContext();
            const response = await assignExternalProgram(payload, programCode, env, isInIframe, b2cAuthResponse, hasInternalPrograms);
            if (!response.ok) return { success: false };
            const data = await response.json();
            if (!data?.success) {
                console.error("External program assignment failed:", data?.message);
                return { success: false };
            }
            window.sessionStorage.setItem("selectedProgramCode", programCode);
            return {
                success: true,
                external_programs: Array.isArray(data?.external_programs) ? data.external_programs : [],
                profile: data?.profile,
                template: data?.template,
            };
        } catch (error) {
            console.error("External program assignment error:", error);
            return { success: false };
        }
    };

    const handleExternalProgramRemoval = async (
        programCode: string,
        authFieldsVal: { [key: string]: string }
    ): Promise<ProgramMutationResult> => {
        const claimOid = getB2CClaims(b2cAuthResponse)?.oid;
        const oid = authFieldsVal?.oid || claimOid;
        if (!programCode || !oid) {
            console.error("Missing programCode or oid for external program removal");
            return { success: false };
        }

        const payload = { ...(authFieldsVal || {}), oid };
        try {
            const { env, isInIframe } = getEnvContext();
            const response = await removeExternalProgram(payload, programCode, env, isInIframe, b2cAuthResponse, hasInternalPrograms);
            if (!response.ok) return { success: false };
            const data = await response.json();
            if (!data?.success) {
                console.error("External program removal failed:", data?.message);
                return { success: false };
            }
            return {
                success: true,
                external_programs: Array.isArray(data?.external_programs) ? data.external_programs : [],
                profile: data?.profile,
                template: data?.template,
            };
        } catch (error) {
            console.error("External program removal error:", error);
            return { success: false };
        }
    };

    const handleCaseRemoval = async (
        caseNumber: string,
        authFieldsVal: { [key: string]: string }
    ): Promise<{ success: boolean; cases?: string[]; profile?: any; template?: any }> => {
        const claimOid = getB2CClaims(b2cAuthResponse)?.oid;
        const oid = authFieldsVal?.oid || claimOid;
        if (!caseNumber || !oid) {
            console.error("Missing caseNumber or oid for case removal");
            return { success: false };
        }

        const payload = { ...(authFieldsVal || {}), oid };
        try {
            const { env, isInIframe } = getEnvContext();
            const response = await removeCase(payload, caseNumber, env, isInIframe, b2cAuthResponse, hasInternalPrograms);
            if (!response.ok) return { success: false };
            const data = await response.json();
            if (!data?.success) {
                console.error("Case removal failed:", data?.message);
                return { success: false };
            }
            return {
                success: true,
                cases: Array.isArray(data?.cases) ? data.cases : [],
                profile: data?.profile,
                template: data?.template,
            };
        } catch (error) {
            console.error("Case removal error:", error);
            return { success: false };
        }
    };

    const handleB2CProfileEdit = async () => {
        if (!msalInstance) {
            console.error("MSAL instance is not initialized.");
            return;
        }

        const profileEditRequest = {
            authority: branding?.azure_b2c_profileflow_endpoint,
            scopes: ["openid", "profile", "email"],
            codeChallengeMethod: "S256",
        };

        try {
            // Open the profile edit page in a popup to preserve the chat session
            const response = await msalInstance.loginPopup(profileEditRequest);
            appStateContext?.dispatch({
                type: 'SET_B2C_AUTH_RESPONSE',
                payload: { instance: msalInstance, response }
            });
        } catch (error) {
            console.error("Profile edit error:", error);
        }
    };

    return {
        handleB2CSignIn,
        handleIframeSignIn,
        handleB2CSignOut,
        handleInternalProgramAssignment,
        handleExternalProgramAssignment,
        handleExternalProgramRemoval,
        handleCaseRemoval,
        handleB2CProfileEdit,
    };
};
